"use strict";

/**
 * DGF-3054 — client-side diagnostics for express wallets.
 *
 * Needed because the interesting failures are invisible to the server. When Google Pay fails its
 * availability check, or a component is created and never mounts, no request is made and no server
 * log line exists — so "never offered" and "offered and refused" look identical from the outside.
 * This is what makes the Android Chrome / Web Component 6.21.0 validation actually diagnosable
 * with Adyen.
 *
 * Three sinks, all optional and all failure-tolerant:
 *   - window.sdExpressDiagnostics — an in-page array, readable from the console during a test
 *   - console.warn                — visible in a shopper's or tester's devtools
 *   - an `express:diagnostic` jQuery event on body — an attachment point for analytics later
 */

const STORE_KEY = "sdExpressDiagnostics";
const EVENT_NAME = "express:diagnostic";
const MAX_ENTRIES = 50;

/**
 * Records one diagnostic entry.
 *
 * @param {Object} deps - injected dependencies ({ win, $ })
 * @param {Object} entry - { surface, wallet, reason, detail }
 * @returns {Object} the recorded entry
 */
function record(deps, entry) {
    const dependencies = deps || {};
    const win = dependencies.win;
    const recorded = Object.assign({ timestamp: Date.now() }, entry);

    if (win) {
        if (!Array.isArray(win[STORE_KEY])) {
            win[STORE_KEY] = [];
        }
        win[STORE_KEY].push(recorded);
        if (win[STORE_KEY].length > MAX_ENTRIES) {
            win[STORE_KEY].shift();
        }
        if (win.console && typeof win.console.warn === "function") {
            win.console.warn(
                `[express] ${recorded.surface || "?"} ${recorded.wallet || "?"}: ${recorded.reason}`,
                recorded.detail || ""
            );
        }
    }

    if (typeof dependencies.$ === "function") {
        try {
            dependencies.$("body").trigger(EVENT_NAME, recorded);
        } catch (error) {
            // Diagnostics must never break a payment surface.
        }
    }

    return recorded;
}

/**
 * Records the outcome of one render pass: why each wallet was skipped, and whether the surface
 * ended up with nothing at all.
 *
 * @param {Object} deps - injected dependencies ({ win, $ })
 * @param {string} surface - "cart", "minicart" or "pdp"
 * @param {Object} result - the walletRenderer result
 * @param {string[]} requestedWallets - wallets the server offered for this surface
 * @returns {void}
 */
function recordRenderResult(deps, surface, result, requestedWallets) {
    if (!result) {
        return;
    }

    (result.skipped || []).forEach((skipped) => {
        record(deps, {
            surface: surface,
            wallet: skipped.wallet,
            reason: skipped.reason
        });
    });

    if ((requestedWallets || []).length && !(result.mounted || []).length) {
        record(deps, {
            surface: surface,
            wallet: requestedWallets.join(","),
            reason: "no-wallet-mounted",
            detail: "server offered wallets but none could be mounted on this device"
        });
    }
}

module.exports = {
    STORE_KEY: STORE_KEY,
    EVENT_NAME: EVENT_NAME,
    MAX_ENTRIES: MAX_ENTRIES,
    record: record,
    recordRenderResult: recordRenderResult
};
